import React, { useState } from 'react';
import { FileText, Copy, Check } from 'lucide-react';
import Button from '../ui/Button';

interface ExecutiveSummarySectionProps {
  summary: string;
  expanded?: boolean;
  maxLength?: number;
  className?: string;
}

export const ExecutiveSummarySection: React.FC<ExecutiveSummarySectionProps> = ({
  summary,
  expanded = false,
  maxLength = 300,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [isCopied, setIsCopied] = useState(false);

  const shouldTruncate = summary.length > maxLength;
  const displayText = isExpanded || !shouldTruncate
    ? summary
    : `${summary.substring(0, maxLength)}...`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600" />
          <h4 className="text-md font-semibold text-neutral-900">
            Executive Summary
          </h4>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="flex items-center gap-1"
        >
          {isCopied ? (
            <>
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-green-600">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Summary Text */}
      <div className="bg-blue-50 border-l-4 border-primary-600 p-4 rounded-r-lg">
        <p className="text-neutral-700 leading-relaxed whitespace-pre-line">
          {displayText}
        </p>

        {/* Read More/Less Button */}
        {shouldTruncate && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {isExpanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExecutiveSummarySection;
